import { useMediaQuery, useTheme } from '@material-ui/core'
import { JIRA_SUPPORT_TICKET_LINK } from 'constants/links'
import { LABELS_FOR_COMPONENT_TYPES } from 'elements/hardwareSelector/HardwareSelector'
import useOptimisticValue from 'elements/input/useOptimisticValue'
import { Translate, useTranslate } from 'react-admin'
import type { OpenSolarThemeType } from 'Themes'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { mapComponentTypesV2ToV1 } from 'util/misc'
import HardwareSelectorTopBanner from '../../../pages/ordering/banner/HardwareSelectorTopBanner'
import { ComponentTypesV2 } from '../../../types/selectComponent'
import HardwareSelectorSearchBar from './HardwareSelectorSearchBar'

const useStyles = makeOpenSolarStyles((theme: OpenSolarThemeType) => ({
  container: {
    width: '100%',
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  description: {
    color: '#0A090B',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    marginBottom: '14px',
  },
  supportText: {
    textAlign: 'center',
    padding: 16,
  },
}))

export const getPlaceholderByComponentType = (componentType: ComponentTypesV2, translate: Translate): string => {
  switch (componentType) {
    case 'module':
      return translate('Search by brand and/or panel code (wrap codes with spaces in "quotes").')
    case 'inverter':
      return translate('Search by brand and/or %{component} code (wrap codes with spaces in "quotes").', {
        component: translate('inverter'),
      })
    case 'battery':
      return translate('Search by brand and/or %{component} code (wrap codes with spaces in "quotes").', {
        component: translate('battery'),
      })
    default:
      return translate('Search by brand and/or %{component} code (wrap codes with spaces in "quotes").', {
        component: translate('component'),
      })
  }
}

const HardwareSelectorToolBars = ({
  description,
  value = '',
  placeholder,
  componentType,
  onChange,
}: {
  description?: string
  value?: string
  placeholder: string
  componentType: ComponentTypesV2
  onChange(search: string): void
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const translate = useTranslate()
  const downLG = useMediaQuery(theme.breakpoints.down('lg'))

  const { optimisticValue, updateOptimisticValue } = useOptimisticValue<string>({ defaultValue: '', value })

  const componentTypeKey = mapComponentTypesV2ToV1(componentType)

  return (
    <div className={classes.container}>
      {!downLG && <HardwareSelectorTopBanner searchText={optimisticValue} componentType={componentType} />}
      {description && <div className={classes.description}>{description}</div>}
      <div className={classes.searchContainer}>
        <HardwareSelectorSearchBar
          placeholder={translate(placeholder)}
          optimisticValue={optimisticValue}
          updateOptimisticValue={updateOptimisticValue}
          onChange={onChange}
        />
        <div className={classes.supportText}>
          {translate(LABELS_FOR_COMPONENT_TYPES[componentTypeKey]?.cannotFind)}{' '}
          <a target="_blank" rel="noopener noreferrer" href={JIRA_SUPPORT_TICKET_LINK}>
            {translate('Let us know')}
          </a>
        </div>
      </div>
    </div>
  )
}

export default HardwareSelectorToolBars
